/**
 * Chutt... - Weather Widget (Radar V2)
 * Metaphore meteo pour le statut de reputation.
 * Mode compact: photo meteo transparente en overlay sur la hero section.
 * Mode full: Paper card avec score et tendance.
 */
import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { colors } from '../../styles/colors';

/**
 * Score → weather image mapping (5 tiers)
 */
function getWeatherImage(score) {
  if (score >= 75) return '/weather/plein-soleil.avif';
  if (score >= 60) return '/weather/soleil-levant.jpg';
  if (score >= 40) return '/weather/nuage.jpg';
  if (score >= 20) return '/weather/pluie.jpg';
  return '/weather/orage.jpg';
}

/**
 * Score → weather state key (for config lookup)
 */
function getWeatherKey(score) {
  if (score >= 70) return 'soleil';
  if (score >= 40) return 'nuage';
  return 'orage';
}

/**
 * Background tint per weather state (very subtle, dark-charcoal compatible).
 */
const weatherConfig = {
  soleil: {
    label: 'Ciel clair',
    insight: 'Image positive dominante. Momentum favorable.',
    bg: 'rgba(16, 185, 129, 0.06)',
    border: 'rgba(16, 185, 129, 0.18)',
    glow: 'rgba(16, 185, 129, 0.12)',
    scoreColor: colors.success,
  },
  nuage: {
    label: 'Nuageux',
    insight: 'Signaux mitiges. Surveillance recommandee.',
    bg: 'rgba(156, 163, 175, 0.06)',
    border: 'rgba(156, 163, 175, 0.18)',
    glow: 'rgba(156, 163, 175, 0.10)',
    scoreColor: colors.gray400,
  },
  orage: {
    label: 'Orage',
    insight: 'Negativite detectee. Action immediate requise.',
    bg: 'rgba(239, 68, 68, 0.06)',
    border: 'rgba(239, 68, 68, 0.18)',
    glow: 'rgba(239, 68, 68, 0.12)',
    scoreColor: colors.danger,
  },
};

function WeatherWidget({ weather, score = 0, trend = 'stable', trendValue = 0, compact = false, forceCrisis = false }) {
  const weatherKey = forceCrisis ? 'orage' : (weather || getWeatherKey(score));
  const config = weatherConfig[weatherKey] || weatherConfig.nuage;

  // 3-color threshold: >=10% green, <=-10% red, between orange
  const trendColor =
    trendValue >= 10 ? colors.success : trendValue <= -10 ? colors.danger : colors.warning;
  const trendBg =
    trendValue >= 10 ? colors.successBg : trendValue <= -10 ? colors.dangerBg : colors.warningBg;

  const trendSign = trendValue > 0 ? '+' : '';

  // Compact overlay mode — full-frame weather photo transparency
  if (compact) {
    const imagePath = forceCrisis ? '/weather/orage.jpg' : getWeatherImage(score);
    const isCrisisOverlay = forceCrisis;
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          overflow: 'hidden',
          borderRadius: 'inherit',
        }}
      >
        <Box
          component="img"
          src={imagePath}
          alt="weather overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isCrisisOverlay ? 0.45 : 0.3,
            mixBlendMode: 'overlay',
          }}
        />
        {/* Bordeaux tint overlay when crisis active */}
        {isCrisisOverlay && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(139,26,26,0.25) 0%, rgba(139,26,26,0.08) 100%)',
              borderRadius: 'inherit',
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${config.border}`,
        bgcolor: config.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle radial glow behind */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${config.glow} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Weather image thumbnail */}
      <Box sx={{ position: 'relative', zIndex: 1, mb: 0.5, width: 64, height: 64, borderRadius: '50%', overflow: 'hidden' }}>
        <Box
          component="img"
          src={getWeatherImage(score)}
          alt={config.label}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>

      {/* Weather label */}
      <Typography
        variant="caption"
        sx={{
          color: colors.gray500,
          fontWeight: 500,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontSize: '0.7rem',
        }}
      >
        {config.label}
      </Typography>

      {/* Score */}
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '2.2rem',
          lineHeight: 1,
          color: config.scoreColor,
          letterSpacing: '-0.02em',
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {score}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: colors.gray400,
          fontSize: '0.7rem',
          mt: -1,
        }}
      >
        / 100
      </Typography>

      {/* Contextual insight */}
      <Typography
        sx={{
          color: colors.gray500,
          fontSize: '0.65rem',
          lineHeight: 1.3,
          textAlign: 'center',
          px: 1,
          mt: 0.5,
        }}
      >
        {config.insight}
      </Typography>

      {/* Trend indicator — no arrows, 3-color */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.2,
          py: 0.4,
          borderRadius: 1,
          bgcolor: trendBg,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: trendColor,
          }}
        >
          {trendSign}{trendValue.toFixed(1)}%
        </Typography>
      </Box>
    </Paper>
  );
}

export default WeatherWidget;
